/**
 * @fileoverview Provides a unique ID generation system using a dictionary of words for the CLI. Manages used and unused IDs, allowing for memorable and reusable identifier creation with a fallback to random string generation when the dictionary is exhausted.
 */

/**
 * An id provider that provides ids from a dictionary of words. This exists
 * to make it easy for the user to remember and use ids in the CLI
 */
export class IdProvider {
  private usedIds: Set<string> = new Set()
  private unusedIds: Set<string> = new Set()

  constructor(usedIds: string[], dictionary: string[] = idWords) {
    this.usedIds = new Set(usedIds)
    this.unusedIds = new Set(dictionary.filter((word) => !this.usedIds.has(word)))
  }

  nextId(): string {
    const id = this.popUnusedId()
    this.usedIds.add(id)
    return id
  }

  recordUsedId(id: string | undefined): void {
    if (!id) {
      return
    }
    this.usedIds.add(id)
    if (this.unusedIds.has(id)) {
      this.unusedIds.delete(id)
    }
  }

  private popUnusedId(): string {
    if (this.unusedIds.size === 0) {
      return Math.random().toString(36).substring(2, 7)
    }
    const id = this.unusedIds.values().next().value as string
    this.unusedIds.delete(id)
    return id
  }
}

const idWords = `able
acid
acre
acts
adam
adds
ages
aide
aims
alan
alex
ally
also
andy
anna
apps
arch
area
army
arts
atom
aunt
auto
away
axis
baby
back
bags
bait
ball
band
bank
bars
base
bath
beam
bean
bear
beds
beef
beer
bell
belt
bend
bike
bill
bird
bite
blog
boat
body
bolt
bomb
bond
bone
book
boom
boot
boss
bowl
boys
brad
brad
bran
brew
bulb
bulk
bull
bush
bust
butt
buzz
cafe
cake
call
calm
camp
cane
cape
card
care
carl
cars
cart
case
cash
cast
cats
cave
cell
cent
chad
chef
chip
city
clay
clip
club
clue
coal
coat
code
coin
cole
cook
cool
cope
copy
cord
core
corn
cost
coup
crew
crop
cube
cure
cuts
dale
dame
dana
dare
dark
data
date
dave
dawn
days
dean
deck
deer
dell
demo
desk
dial
dice
diet
dirt
disc
dish
disk
dock
docs
does
dogs
doll
dome
door
dose
doug
dove
down
drag
draw
drew
drop
drug
drum
dual
duck
duke
dust
duty
ears
ease
east
easy
edge
eggs
eric
even
ever
evil
exam
exit
expo
eyes
face
fact
fade
fail
fair
fall
fame
fans
farm
fate
fear
feed
feel
fees
feet
fell
felt
file
fill
film
find
fine
fire
firm
fish
fist
fits
five
flag
flat
flee
flew
flex
flip
flow
foam
foil
fold
folk
food
fool
foot
ford
fork
form
fort
four
free
frog
from
fuel
full
fund
funk
fury
fuss
gain
game
gang
gaps
gary
gate
gave
gear
gene
gift
girl
give
glad
glen
goal
goat
gods
goes
gold
golf
gone
good
gore
grab
gram
gray
grew
grey
grid
grin
grip
grow
gulf
guru
guys
hair
half
hall
hand
hang
hans
hard
harm
hate
have
head
heal
heap
hear
heat
heel
held
hell
help
herb
herd
here
hero
hide
high
hill
hint
hire
hold
hole
holy
home
hood
hope
horn
host
hour
huge
hugh
hugo
hunt
hurt
icon
idea
idle
inch
info
into
iron
item
jack
jail
jake
jane
jazz
jean
jeff
jill
jobs
joel
john
join
joke
jose
jump
june
jury
just
kate
keep
kent
kept
keys
kick
kids
kill
kind
king
kiss
kits
knee
knew
know
labs
lace
lack
lady
laid
lake
lamb
lamp
land
lane
last
late
lawn
laws
lead
leaf
lean
leap
left
legs
lend
lens
less
lets
liar
lies
life
lift
like
lime
line
link
lion
lisa
list
live
load
loan
lock
loft
logo
long
look
loop
lord
lose
loss
lost
lots
loud
love
luck
lucy
luke
lung
made
mail
main
make
male
mall
many
maps
mark
mars
mart
mary
mask
mass
mate
math
matt
meal
mean
meat
meet
menu
mere
mesh
mess
mice
mike
mile
milk
mill
mind
mine
mini
mint
miss
mode
mood
moon
more
moss
most
move
much
must
myth
nail
name
navy
near
neck
need
nest
news
next
nice
nick
nine
node
none
nose
note
noun
nuts
odds
ohio
oils
okay
once
only
onto
open
oral
otto
ours
oval
oven
over
pace
pack
page
paid
pain
pair
palm
park
part
pass
past
path
paul
peak
pear
peer
pete
pets
pick
pile
pill
pine
pink
pipe
plan
play
plot
plug
plus
poem
poet
pole
poll
polo
pond
pool
poor
pope
port
post
pour
pray
prep
prey
prof
prop
pull
pump
pure
push
puts
quad
quit
quiz
race
rack
rail
rain
rank
rare
rate
rays
read
real
rear
reed
reef
rely
rent
rest
rice
rich
rick
ride
ring
rise
risk
road
rock
role
roll
roof
room
root
rope
rose
ross
rule
rush
ruth
ryan
safe
said
sake
sale
salt
same
sand
save
says
scan
scar
scot
seal
seat
seed
seek
seem
seen
self
sell
send
sent
sets
sexy
shed
ship
shoe
shop
shot
show
shut
sick
side
sign
silk
sing
sink
site
size
skin
skip
slam
slap
slip
slot
slow
snap
snow
soap
sofa
soft
soil
sold
sole
some
song
sons
soon
sort
soul
soup
spam
span
spin
spot
star
stay
stem
step
stop
such
suck
suit
sure
swim
tabs
tail
take
tale
talk
tall
tank
tape
task
team
tech
teen
tell
tend
term
test
text
than
that
them
then
they
thin
this
thou
time
tiny
tips
tire
todd
toll
tone
tony
took
tool
tops
tour
town
trap
tray
tree
trek
trim
trip
troy
true
tube
tune
turn
twin
type
ugly
unit
upon
urge
used
user
uses
utah
vary
vast
very
vice
view
visa
void
vote
wade
wage
wait
wake
walk
wall
want
ward
warm
wars
wash
wave
ways
weak
wear
weed
week
well
went
were
west
what
when
whom
wide
wife
wild
will
wind
wine
wing
wins
wire
wise
wish
with
wolf
wood
word
wore
work
worm
worn
wrap
yard
yarn
yeah
year
your
zero
zone
zoom`
  .split('\n')
  .sort(() => Math.random() - 0.5)
